import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'
import {
  type CustomChangeBroadcastPayload,
  listenToChangesOnForm,
  fieldIsMultiple,
  shouldSkipCheckForChanges,
  urlFromDependencyData,
  dependencyWithModelParent
} from '../_input/_broadcast_change_utils'

export default class extends Controller<HTMLDivElement> {
  static targets = ['spinner', 'input', 'frame']

  static values = {
    dependency: Object,
    lazyLoadUrl: String,
    modelParent: String,
    initialDependencyTriggerDelay: Number,
    additionalParameters: Object,
    id: String,
    fetchErrorHtml: String
  }

  declare readonly spinnerTarget: HTMLDivElement
  declare readonly frameTarget: HTMLElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly hasInputTarget: boolean

  declare readonly dependencyValue: { depend_on: string[]; from_path: string; frame_id: string }
  declare readonly hasDependencyValue: boolean
  declare readonly lazyLoadUrlValue: string
  declare readonly hasLazyLoadUrlValue: boolean
  declare readonly modelParentValue: string
  declare readonly initialDependencyTriggerDelayValue: number
  declare readonly additionalParametersValue: Record<string, string>
  declare readonly idValue: string
  declare readonly fetchErrorHtmlValue: string
  declare readonly hasFetchErrorHtmlValue: boolean

  connect(): void {
    this.#listenToFetchChangesForSpinnerVisibilityToggle()

    if (this.hasDependencyValue && this.hasInputTarget) {
      listenToChangesOnForm(
        this.inputTarget.form,
        this.#checkInputValueChangesWithinBroadcast.bind(this),
        false
      )

      if (this.initialDependencyTriggerDelayValue !== -1) {
        window.setTimeout(() => {
          this.#checkInputValueChangesWithinBroadcast(null)
        }, this.initialDependencyTriggerDelayValue)
      }
    }

    if (this.hasLazyLoadUrlValue) {
      Turbo.visit(this.lazyLoadUrlValue, { frame: this.idValue })
    }
  }

  #listenToFetchChangesForSpinnerVisibilityToggle(): void {
    this.frameTarget.addEventListener('turbo:before-fetch-request', () => {
      this.spinnerTarget.classList.remove('hidden')
    })

    this.frameTarget.addEventListener('turbo:before-fetch-response', () => {
      this.spinnerTarget.classList.add('hidden')
    })
  }

  #checkInputValueChangesWithinBroadcast(eventPayload: CustomChangeBroadcastPayload | null): void {
    const isMultiple = fieldIsMultiple(eventPayload)
    const { from_path: path, depend_on: dependOn } = this.dependencyValue
    const dependenciesToCheck =
      this.modelParentValue !== ''
        ? dependencyWithModelParent(dependOn, this.modelParentValue)
        : dependOn
    if (shouldSkipCheckForChanges(eventPayload, isMultiple, dependenciesToCheck)) return

    const url = urlFromDependencyData(
      path,
      this.inputTarget.form,
      dependOn,
      this.modelParentValue,
      this.additionalParametersValue
    )
    if (url !== null) {
      Turbo.visit(url, { frame: this.idValue })
    }
  }
}
